/**
 * Root entry component for unauthenticated `/` route.
 *
 * ACT-INDEEP-ROOT-MOBILE-LOGIN01: Make mobile sign-in the unauthenticated root screen.
 * ACT-INDEEP-AUTH-UNIFIED-ENTRY01: Make `registration_kind=unclassified` →
 *   `/profile-completion` routing explicit at the canonical routing authority
 *   (this file), so the journey is deterministic instead of depending on
 *   chained fallthroughs.
 *
 * Route contract:
 * - Unauthenticated user visiting `/` → Shows MobileLoginPage directly
 * - Authenticated user with registration_kind=unclassified → /profile-completion
 * - Authenticated user with any other kind → Redirects to /discover
 * - Browser URL stays `/` for unauthenticated users (no redirect to /login/mobile)
 *
 * This component is the canonical entry point for unauthenticated users,
 * replacing the old "Welcome to InDeep" marketing page with the mobile login screen.
 *
 * Security posture:
 * - No sensitive data exposed
 * - Auth state is derived from server validation via useAuthStore
 * - Loading state shows nothing (prevents flash of content)
 */
import { Navigate } from 'react-router-dom';
import { MobileLoginPage } from '@/pages/MobileLoginPage';
import { useAuthStore } from '@/store/authStore';
import { RoutePaths } from '@/routes';

/**
 * Root entry component - auth-aware routing for `/`.
 *
 * - Loading: Returns null (no flash of content)
 * - Authenticated + unclassified: Redirects to /profile-completion (GAP-C closure)
 * - Authenticated + any other kind: Redirects to /discover (main app)
 * - Anonymous: Shows MobileLoginPage directly
 */
export function RootEntry() {
  const authStatus = useAuthStore((state) => state.authStatus);
  const registrationKind = useAuthStore((state) => state.registrationKind);

  // Loading state: show nothing to avoid flash of content
  // The bootstrap will complete and re-render with correct state
  if (authStatus === 'loading' || authStatus === 'unknown') {
    return null;
  }

  // Authenticated users: route by registrationKind.
  // ACT-INDEEP-AUTH-UNIFIED-ENTRY01 GAP-C closure: an authenticated
  // unclassified user (newly signed up via the unified entry surface) is
  // routed explicitly to /profile-completion rather than relying on the
  // downstream HomePage useHomeExperience() branch — that branch remains
  // as defense in depth, but the canonical authority for "you just
  // verified, where do you go" is THIS file.
  if (authStatus === 'authenticated') {
    if (registrationKind === 'unclassified') {
      return <Navigate to={RoutePaths.PROFILE_COMPLETION} replace />;
    }
    return <Navigate to={RoutePaths.DISCOVER} replace />;
  }

  // Anonymous users: show mobile login screen directly
  // URL stays as `/` (not redirected to /login/mobile)
  return <MobileLoginPage />;
}
